import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SendIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'feature' | 'bug' | 'feedback' | 'support';
}
const feedbackSchema = z.object({
  subject: z.string().min(3, 'Subject must be at least 3 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  email: z.string().email('Please enter a valid email address').optional()
});
type FeedbackFormData = z.infer<typeof feedbackSchema>;
const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  type
}) => {
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    },
    reset
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema)
  });
  const getModalConfig = () => {
    switch (type) {
      case 'feature':
        return {
          title: 'Request a Feature',
          description: 'Tell us what feature you would like to see',
          subjectPlaceholder: 'e.g., Bulk import assessments',
          icon: '✨'
        };
      case 'bug':
        return {
          title: 'Report a Bug',
          description: 'Help us fix issues by reporting bugs',
          subjectPlaceholder: 'e.g., Unable to save assessment',
          icon: '🐛'
        };
      case 'feedback':
        return {
          title: 'Give Feedback',
          description: 'Share your thoughts and suggestions',
          subjectPlaceholder: 'e.g., Great experience with...',
          icon: '💬'
        };
      case 'support':
        return {
          title: 'Contact Support',
          description: 'Get help from our support team',
          subjectPlaceholder: 'e.g., Question about pricing',
          icon: '📧'
        };
    }
  };
  const config = getModalConfig();
  const onSubmit = async (data: FeedbackFormData) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Submitted:', {
      type,
      ...data
    });
    reset();
    onClose();
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1434">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-1435">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-lg bg-surface-elevated rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1436">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-custom" data-id="element-1437">
            <div className="flex items-center gap-3" data-id="element-1438">
              <span className="text-3xl" data-id="element-1439">{config.icon}</span>
              <div data-id="element-1440">
                <h3 className="text-xl font-bold text-text-primary" data-id="element-1441">
                  {config.title}
                </h3>
                <p className="text-sm text-text-secondary mt-1" data-id="element-1442">
                  {config.description}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface transition-colors" aria-label="Close modal" data-id="element-1443">
              <XIcon size={20} className="text-text-secondary" data-id="element-1444" />
            </button>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4" data-id="element-1445">
            {/* Email Field (Optional) */}
            <div data-id="element-1446">
              <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2" data-id="element-1447">
                Email <span className="text-text-tertiary" data-id="element-1448">(optional)</span>
              </label>
              <input {...register('email')} type="email" id="email" placeholder="your@email.com" className="w-full px-4 py-2.5 bg-surface border border-custom rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent transition-all" data-id="element-1449" />
              {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-1450">
                  {errors.email.message}
                </p>}
            </div>
            {/* Subject Field */}
            <div data-id="element-1451">
              <label htmlFor="subject" className="block text-sm font-medium text-text-primary mb-2" data-id="element-1452">
                Subject <span className="text-red-500" data-id="element-1453">*</span>
              </label>
              <input {...register('subject')} type="text" id="subject" placeholder={config.subjectPlaceholder} className="w-full px-4 py-2.5 bg-surface border border-custom rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent transition-all" data-id="element-1454" />
              {errors.subject && <p className="mt-1 text-sm text-red-600" data-id="element-1455">
                  {errors.subject.message}
                </p>}
            </div>
            {/* Description Field */}
            <div data-id="element-1456">
              <label htmlFor="description" className="block text-sm font-medium text-text-primary mb-2" data-id="element-1457">
                Description <span className="text-red-500" data-id="element-1458">*</span>
              </label>
              <textarea {...register('description')} id="description" rows={5} placeholder="Provide as much detail as possible..." className="w-full px-4 py-2.5 bg-surface border border-custom rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent transition-all resize-none" data-id="element-1459" />
              {errors.description && <p className="mt-1 text-sm text-red-600" data-id="element-1460">
                  {errors.description.message}
                </p>}
            </div>
            {/* Action Buttons */}
            <div className="flex gap-3 pt-2" data-id="element-1461">
              <button type="submit" disabled={isSubmitting} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#00B75F] to-[#00a055] text-white font-semibold rounded-xl shadow-green-glow hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1462">
                {isSubmitting ? <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1463" />
                    Sending...
                  </> : <>
                    <SendIcon size={18} data-id="element-1464" />
                    Submit
                  </>}
              </button>
              <button type="button" onClick={onClose} className="px-6 py-3 border-2 border-custom text-text-secondary rounded-xl font-semibold hover:bg-surface transition-all" data-id="element-1465">
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
};
export default FeedbackModal;